/// <amd-module name="Artifacts/Upack/redux/Sagas" />
import {
  BatchGenerator,
  IBatchIterator,
  TransformingBatchGenerator,
} from "Artifacts/Package/services/BatchHandling";
import { PerfScenarios } from "Artifacts/Root/Constants";
import { FeedPerformanceHelper } from "Artifacts/Root/helpers/FeedPerformanceHelper";
import {
  IDeleteActionPayload,
  IPromoteActionPayload,
  IRestoreActionPayload,
} from "Artifacts/Root/Protocols/types/ICommandActionPayloads";
import { RootActions } from "Artifacts/Root/redux/Actions";
import { call, getContext, put, takeEvery } from "Artifacts/Root/Saga";
import { IPackageVersionIdentity } from "Artifacts/Root/types";
import { MinimalPackageDetails } from "Artifacts/Root/types/PackagingShared";
import { UpackActionTypes } from "Artifacts/Upack/redux/Actions";
import { IFeedUPackDataService } from "Artifacts/Upack/services/UPackDataService";
import { SagaIterator } from "redux-saga";
import { ActionWithPayload } from "redux/Utils/ActionHelper";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssScenario } from "VSS/Platform/Performance";

export function* rootSaga(): SagaIterator {
  yield takeEvery(UpackActionTypes.Promote, promote);
  yield takeEvery(UpackActionTypes.Restore, restore);
  yield takeEvery(UpackActionTypes.SoftDelete, softDelete);
  yield takeEvery(UpackActionTypes.PermanentDelete, permanentDelete);
}

export function* promote(
  action: ActionWithPayload<UpackActionTypes.Promote, IPromoteActionPayload>,
): Generator {
  try {
    yield put(RootActions.promoteCommandStarted());
    const { feed, versions, viewId, onSuccessCallback } = action.payload;

    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
    const dataService = pageContext.getService<IFeedUPackDataService>("IFeedUPackDataService");
    const iterator: IBatchIterator<MinimalPackageDetails[]> = new BatchGenerator(
      versions.map((v) => {
        return {
          id: v.name,
          version: v.version,
        } as MinimalPackageDetails;
      }),
      dataService.batchSize,
    );

    const projectId = feed.project ? feed.project.id : null;

    yield call([dataService, dataService.batchPromote], feed.id, viewId, iterator, projectId);

    // retrieve the view so the name can be applied on the selected versions.
    const view = feed.views.find((v) => v.id === viewId);

    yield put(RootActions.promoteCommandCompleted());
    yield call(onSuccessCallback, versions, view?.name);
  } catch (error) {
    yield put(RootActions.promoteCommandFailed(error.message));
  }
}

export function* softDelete(
  action: ActionWithPayload<UpackActionTypes.SoftDelete, IDeleteActionPayload>,
): Generator {
  try {
    yield put(RootActions.softDeleteCommandStarted());
    const { feed, versions, onSuccessCallback } = action.payload;

    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
    const dataService = pageContext.getService<IFeedUPackDataService>("IFeedUPackDataService");

    const iterator = new TransformingBatchGenerator(
      versions,
      dataService.batchSize,
      (version: IPackageVersionIdentity) => {
        return {
          id: version.name,
          version: version.version,
        };
      },
    );
    const projectId = feed.project ? feed.project.id : null;

    yield call([dataService, dataService.batchDelete], feed.id, iterator, projectId);

    yield put(RootActions.softDeleteCommandCompleted());
    yield call(onSuccessCallback, versions);
  } catch (error) {
    yield put(RootActions.softDeleteCommandFailed(error.message));
  }
}

export function* restore(
  action: ActionWithPayload<UpackActionTypes.Restore, IRestoreActionPayload>,
): Generator {
  yield put(RootActions.restoreCommandStarted());
  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  let perfScenario: IVssScenario | undefined;
  try {
    const { feed, versions, onSuccessCallback } = action.payload;

    const isSingleVersion = versions.length === 1;
    perfScenario = FeedPerformanceHelper.startScenario(
      pageContext,
      isSingleVersion
        ? PerfScenarios.RecycleBinPackageVersionRestore
        : PerfScenarios.MultiplePackageVersionsRestoredToFeed,
    );

    const dataService = pageContext.getService<IFeedUPackDataService>("IFeedUPackDataService");

    const iterator = new TransformingBatchGenerator(
      versions,
      dataService.batchSize,
      (packageVersion: IPackageVersionIdentity) => {
        return {
          id: packageVersion.name,
          version: packageVersion.version,
        };
      },
    );

    const projectId = feed.project ? feed.project.id : null;

    yield call([dataService, dataService.batchRestoreToFeed], feed.id, iterator, projectId);

    FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    yield put(RootActions.restoreCommandCompleted());
    yield call(onSuccessCallback, versions);
  } catch (error) {
    if (perfScenario) {
      FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    }
    yield put(RootActions.restoreCommandFailed(error.message));
  }
}

export function* permanentDelete(
  action: ActionWithPayload<UpackActionTypes.PermanentDelete, IDeleteActionPayload>,
): Generator {
  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  let perfScenario: IVssScenario | undefined;
  try {
    yield put(RootActions.permDeleteCommandStarted());
    const { feed, versions, onSuccessCallback } = action.payload;

    const isSingleVersion = versions.length === 1;
    perfScenario = FeedPerformanceHelper.startScenario(
      pageContext,
      isSingleVersion
        ? PerfScenarios.RecycleBinPackageVersionPermanentDelete
        : PerfScenarios.MultiplePackageVersionsPermanentDeleted,
    );

    const dataService = pageContext.getService<IFeedUPackDataService>("IFeedUPackDataService");
    const iterator: IBatchIterator<MinimalPackageDetails[]> = new BatchGenerator(
      versions.map((v) => {
        return {
          id: v.name,
          version: v.version,
        } as MinimalPackageDetails;
      }),
      dataService.batchSize,
    );

    const projectId = feed.project ? feed.project.id : null;

    yield call([dataService, dataService.batchPermanentlyDelete], feed.id, iterator, projectId);

    FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    yield put(RootActions.permDeleteCommandCompleted());
    yield call(onSuccessCallback, versions);
  } catch (error) {
    if (perfScenario) {
      FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    }
    yield put(RootActions.permDeleteCommandFailed(error.message));
  }
}
